// Normalized vocabulary shared by every integration. See spec/algorithm.md §2.

export const EVENT_TYPES = [
  'session_started',
  'prompt_submitted',
  'tool_started',
  'tool_finished',
  'permission_requested',
  'permission_resolved',
  'turn_completed',
  'turn_failed',
  'interrupted',
  'needs_input',
  'session_ended',
  'heartbeat',
] as const;

export type EventType = (typeof EVENT_TYPES)[number];

export type EventSource = 'hook' | 'log' | 'cli' | 'api' | 'otel' | 'inferred';

export interface EventContext {
  cwd?: string;
  repoName?: string;
  gitRemote?: string;
  gitBranch?: string;
  model?: string;
  machine?: string;
  providerEvent?: string;
  providerVersion?: string;
  originator?: string;
  agentId?: string;
  /** Provider session id of the parent (subagent threads that run as their own session). */
  parentSession?: string;
  turnId?: string;
  pid?: number;
  /** Opt-in only: a short title derived from the first prompt on the user's machine. Never synced. */
  title?: string;
}

export interface TallyEvent {
  id: string;
  /** Epoch milliseconds, UTC. */
  at: number;
  seq?: number;
  provider: string;
  session: string;
  type: EventType;
  source?: EventSource;
  toolUseId?: string;
  reason?: string;
  context?: EventContext;
}

export type SessionState = 'working' | 'waiting_input' | 'waiting_permission' | 'idle' | 'ended';
export type SpanState = Exclude<SessionState, 'ended'>;
export type IdleReason = 'stale' | 'input_timeout';

export interface Span {
  state: SpanState;
  start: number;
  /** `null` while the span is open. */
  end: number | null;
  reason: IdleReason | null;
  eventIds: string[];
}

export interface EngineConfig {
  idleTimeoutMs: number;
  staleGraceMs: number;
  minSpanMs: number;
  mergeGapMs: number;
  inputIdleAfterMs: number;
}

export const DEFAULT_CONFIG: Readonly<EngineConfig> = Object.freeze({
  idleTimeoutMs: 120_000,
  staleGraceMs: 15_000,
  minSpanMs: 2_000,
  mergeGapMs: 5_000,
  inputIdleAfterMs: 900_000,
});

export interface SessionTotals {
  countedMs: number;
  waitingInputMs: number;
  waitingPermissionMs: number;
  idleMs: number;
}

export interface SessionResult {
  /** `null` before the first event. */
  state: SessionState | null;
  stateSince: number | null;
  lastActivityAt: number | null;
  lastEventAt: number | null;
  spans: Span[];
  totals: SessionTotals;
}

export interface Interval {
  start: number;
  end: number;
}
